import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import profilePic from './profile_circle.png';

const CASE_STUDIES = [
  {
    id: 'retail',
    cat: 'Retail · Cloud Migration',
    title: 'Retail Cloud Migration & Modernization',
    metric: '30% OpEx reduction · 99.99% Uptime',
    icon: '🏪',
    badgeClass: 'thumb-retail',
    summary: 'Led full-scale infrastructure migration for a major retail chain, replacing legacy monoliths with resilient cloud-native microservices.',
    challenge: 'Legacy core systems suffered from high infrastructure costs, frequent seasonal downtime, and poor elasticity during peak holiday shopping surges.',
    solution: 'Designed and executed a multi-region cloud architecture leveraging AWS serverless, automated CI/CD pipelines, and automated FinOps governance controls.',
    impacts: [
      '30% reduction in annual cloud & operational expenditures (OpEx)',
      'Achieved 99.99% system availability through high-concurrency peak retail events',
      'Accelerated feature deployment velocity from monthly releases to daily automated deployments'
    ],
    tech: ['AWS Lambda', 'ECS / Kubernetes', 'Terraform', 'FinOps', 'DynamoDB', 'CloudFront']
  },
  {
    id: 'ai',
    cat: 'Enterprise · AI Strategy',
    title: 'Enterprise AI Strategy & Governance',
    metric: '25% productivity gain · ISO-aligned AI Policy',
    icon: '🤖',
    badgeClass: 'thumb-ai',
    summary: 'Developed an enterprise-wide AI governance framework and secure GenAI workflow engine for global cross-functional teams.',
    challenge: 'Heterogeneous shadow AI usage created data security risks, compliance hurdles, and redundant vendor spend across business units.',
    solution: 'Engineered a centralized AI Hub with Retrieval-Augmented Generation (RAG), strict data anonymization, role-based access control, and model evaluation standards.',
    impacts: [
      '25% boost in average employee task completion speed across internal operations',
      'Zero compliance incidents with end-to-end audit logging for sensitive prompts & model output',
      'Saved estimated $2.4M in consolidated AI vendor licensing costs'
    ],
    tech: ['Generative AI', 'Claude & Anthropic APIs', 'Python / LangChain', 'Vector DBs (Pinecone/pgvector)', 'AWS Bedrock', 'LLM Governance']
  },
  {
    id: 'logistics',
    cat: 'Logistics · Modernisation',
    title: 'Real-Time Logistics & Supply Chain Engine',
    metric: '40% latency reduction · 5,000+ Nodes',
    icon: '🚚',
    badgeClass: 'thumb-logistics',
    summary: 'Architected a real-time, event-driven cloud platform that powers dynamic tracking and predictive delivery scheduling.',
    challenge: 'Batch-processed logistics data caused 6-8 hour delays in supply chain status reporting, leading to missed delivery windows and high overhead.',
    solution: 'Re-engineered backend architecture around Apache Kafka event streaming, containerized microservices on GCP, and real-time telemetry analytics.',
    impacts: [
      '40% decrease in supply chain end-to-end data latency',
      'Real-time visibility for 5,000+ active transit nodes across regional hubs',
      'Improved on-time delivery rate from 88% to 96.5%'
    ],
    tech: ['GCP Cloud', 'Apache Kafka', 'Kubernetes', 'Go / Node.js', 'PostgreSQL', 'Redis']
  }
];

function App() {
  const observerRef = useRef(null);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [activeSection, setActiveSection] = useState('home');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '', subject: 'Enterprise Architecture Inquiry' });
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Trigger temporary toast
  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  // Copy helper
  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    showToast(`${label} copied to clipboard!`);
  };

  // Print CV trigger
  const handleDownloadCV = () => {
    window.print();
  };

  useEffect(() => {
    // Intersection observer for fade animations
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observerRef.current.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.fade-up').forEach((el) => {
      observerRef.current.observe(el);
    });

    // Scroll listener for nav & scroll-top button
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);

      const sections = ['home', 'about', 'impact', 'experience', 'skills', 'insights', 'contact'];
      const scrollPosition = window.scrollY + 200;

      for (const sectionId of sections) {
        const el = document.getElementById(sectionId);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => {
      setFormSubmitted(false);
      setContactModalOpen(false);
      setContactForm({ name: '', email: '', message: '', subject: 'Enterprise Architecture Inquiry' });
      showToast('Thank you! Your message has been sent.');
    }, 1200);
  };

  return (
    <div className="App">

      {/* TOAST NOTIFICATION */}
      {toastMsg && (
        <div className="toast-notification">
          <span className="toast-icon">✓</span>
          <span>{toastMsg}</span>
        </div>
      )}

      {/* NAV */}
      <header className="header print-hide">
        <a href="#home" className="logo">TB<span>.</span></a>
        <nav className="nav-menu">
          <a href="#about" className={activeSection === 'about' ? 'active' : ''}>About</a>
          <a href="#impact" className={activeSection === 'impact' ? 'active' : ''}>Impact</a>
          <a href="#experience" className={activeSection === 'experience' ? 'active' : ''}>Experience</a>
          <a href="#skills" className={activeSection === 'skills' ? 'active' : ''}>Skills</a>
          <a href="#insights" className={activeSection === 'insights' ? 'active' : ''}>Insights</a>
          <button className="nav-cv-btn" onClick={handleDownloadCV} title="Print or Save CV PDF">
            <span>📄</span> Download CV
          </button>
          <button className="nav-cta" onClick={() => setContactModalOpen(true)}>
            Let's Talk
          </button>
        </nav>
      </header>

      {/* HERO */}
      <section className="hero" id="home">
        <div className="hero-grid-bg print-hide"></div>
        <div className="hero-glow print-hide"></div>
        <div className="hero-inner">
          <div className="hero-profile fade-up">
            <img src={profilePic} alt="Tirthankar Bhadra" className="profile-img" />
          </div>

          <div className="hero-main">
            <div className="hero-content">
              <div className="hero-tag fade-up">
                <span className="pulse-dot"></span>
                Enterprise Cloud Architect · AI Leader
              </div>
              <h1 className="fade-up">Tirthankar<br /><em>Bhadra</em></h1>
              <p className="hero-sub fade-up">
                Architecting the future of enterprise AI &amp; scalable cloud solutions —
                bridging complex technical delivery with large-scale business strategy.
              </p>
              <div className="hero-buttons fade-up print-hide">
                <a href="#impact" className="btn btn-primary">View Impact Portfolio →</a>
                <button onClick={handleDownloadCV} className="btn btn-secondary">
                  <span>📄</span> Download PDF CV
                </button>
                <button onClick={() => setContactModalOpen(true)} className="btn btn-outline">
                  Let's Connect
                </button>
              </div>
            </div>
          </div>

          <div className="hero-stats fade-up">
            <div className="stat">
              <span className="stat-num">21+</span>
              <span className="stat-label">Years Experience</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat">
              <span className="stat-num">3</span>
              <span className="stat-label">Top Global Consultancies</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat">
              <span className="stat-num">$10M+</span>
              <span className="stat-label">Transformation Programs</span>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNALS BAR */}
      <div className="signals-bar print-hide">
        <div className="marquee-track">
          {[
            'TOGAF® Certified',
            'Claude Certified (CCA-F)',
            'AWS Solutions Architect',
            'Google Cloud Professional',
            'Generative AI Leader',
            'Enterprise Architecture',
            'Digital Transformation',
            'TOGAF® Certified',
            'Claude Certified (CCA-F)',
            'AWS Solutions Architect',
            'Google Cloud Professional',
            'Generative AI Leader',
            'Enterprise Architecture',
            'Digital Transformation',
          ].map((item, i) => (
            <span key={i} className="marquee-item">
              <span className="marquee-star">★</span>
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ABOUT */}
      <section className="about-section" id="about">
        <div className="section-label">Who I am</div>
        <h2 className="section-title">Strategic architect,<br /><em>practical leader</em></h2>
        <div className="about-grid">
          <div className="about-text fade-up">
            <p>
              Strategic technology leader with over two decades of experience driving high-stakes digital
              transformations across <strong>TCS, Deloitte, and IBM</strong>. I bridge the gap between complex
              technical delivery and large-scale business strategy.
            </p>
            <p>
              My specialization spans <strong>AI enablement, resilient cloud architectures</strong>, and
              enterprise-wide modernisation programs — consistently delivering outcomes that move
              the needle for Fortune 500 clients.
            </p>
            <p>
              Whether it's defining multi-cloud strategies, governing AI frameworks, or leading
              cross-functional engineering teams — I bring a systems-thinking mindset to every
              challenge.
            </p>
          </div>
          <div className="about-card fade-up">
            <div className="about-card-row">
              <div className="icon-box gold">🏛️</div>
              <div>
                <div className="card-label">Current Role</div>
                <div className="card-value">Enterprise Architect — TCS</div>
              </div>
            </div>
            <div className="about-card-row">
              <div className="icon-box blue">🎓</div>
              <div>
                <div className="card-label">Certifications</div>
                <div className="card-value">TOGAF · Claude CCA-F · AWS · GCP</div>
              </div>
            </div>
            <div className="about-card-row">
              <div className="icon-box green">✉️</div>
              <div>
                <div className="card-label">Email</div>
                <div className="card-value">
                  <a href="mailto:tirtha2u@gmail.com">tirtha2u@gmail.com</a>
                  <button className="copy-small-btn" onClick={() => handleCopy('tirtha2u@gmail.com', 'Email')}>Copy</button>
                </div>
              </div>
            </div>
            <div className="about-card-row">
              <div className="icon-box cyan">📱</div>
              <div>
                <div className="card-label">Phone</div>
                <div className="card-value">
                  <a href="tel:+919831950061">+91 9831950061</a>
                  <button className="copy-small-btn" onClick={() => handleCopy('+919831950061', 'Phone number')}>Copy</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="impact-section" id="impact">
        <div className="section-label">Selected work</div>
        <h2 className="section-title">Impact <em>portfolio</em></h2>
        <p className="section-desc">Measurable outcomes from enterprise engagements. Click any case study for details.</p>
        <div className="impact-grid">
          {CASE_STUDIES.map((cs) => (
            <div
              key={cs.id}
              className="impact-card fade-up clickable-card"
              onClick={() => setSelectedCaseStudy(cs)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setSelectedCaseStudy(cs)}
            >
              <div className={`impact-thumb ${cs.badgeClass}`}>
                <div className="thumb-grid print-hide"></div>
                <span className="thumb-icon">{cs.icon}</span>
                <span className="card-click-hint">Click for Case Study ↗</span>
              </div>
              <div className="impact-body">
                <div className="impact-cat">{cs.cat}</div>
                <h3>{cs.title}</h3>
                <p>{cs.summary}</p>
                <div className="impact-footer">
                  <div className="impact-metric">{cs.metric}</div>
                  <span className="learn-more-link">View Details →</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EXPERIENCE */}
      <section className="exp-section" id="experience">
        <div className="section-label">Career path</div>
        <h2 className="section-title">Strategic <em>experience</em></h2>
        <div className="exp-timeline">
          <div className="exp-item fade-up">
            <div className="exp-date">2011 — PRESENT</div>
            <div className="exp-content">
              <div className="exp-company-badge tcs">TCS</div>
              <h3>Enterprise Architect — Cloud &amp; AI</h3>
              <div className="exp-company">Tata Consultancy Services</div>
              <ul className="exp-list">
                <li>Spearheaded multi-cloud transformation programs for Tier-1 financial institutions across North America & APAC.</li>
                <li>Architected resilient cloud migration roadmaps delivering 30%+ infrastructure cost optimization and zero zero-day downtime.</li>
                <li>Established governance guardrails and FinOps practice for multi-cloud enterprise deployments.</li>
              </ul>
              <div className="tags">
                <span className="tag">Multi-Cloud</span>
                <span className="tag">FinOps</span>
                <span className="tag">Architecture Governance</span>
                <span className="tag">Enterprise AI</span>
              </div>
            </div>
          </div>
          <div className="exp-item fade-up">
            <div className="exp-date">2010 — 2011</div>
            <div className="exp-content">
              <div className="exp-company-badge deloitte">Deloitte</div>
              <h3>Manager — Digital Transformation &amp; Cloud</h3>
              <div className="exp-company">Deloitte Consulting</div>
              <ul className="exp-list">
                <li>Managed $10M+ digital transformation engagements for global public sector & enterprise clients.</li>
                <li>Defined enterprise architecture standards aligned with TOGAF 9.2 framework across multi-disciplinary teams.</li>
                <li>Led cloud strategy advisory, legacy modernisations, and executive-level client workshops.</li>
              </ul>
              <div className="tags">
                <span className="tag">$10M+ Programs</span>
                <span className="tag">Public Sector</span>
                <span className="tag">TOGAF</span>
                <span className="tag">Cloud Strategy</span>
              </div>
            </div>
          </div>
          <div className="exp-item fade-up">
            <div className="exp-date">2003 — 2010</div>
            <div className="exp-content">
              <div className="exp-company-badge ibm">IBM</div>
              <h3>Lead AI &amp; Cognitive Solutions Architect</h3>
              <div className="exp-company">IBM India / Global Business Services</div>
              <ul className="exp-list">
                <li>Spearheaded applied R&amp;D and solution delivery for IBM Watson cognitive intelligence platforms.</li>
                <li>Engineered machine learning pipelines and real-time analytics solutions for enterprise clients.</li>
                <li>Mentored engineering teams and authored technical design specifications for cloud deployments.</li>
              </ul>
              <div className="tags">
                <span className="tag">Cognitive AI</span>
                <span className="tag">IBM Watson</span>
                <span className="tag">MLOps</span>
                <span className="tag">Analytics</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SKILLS */}
      <section className="skills-section" id="skills">
        <div className="section-label">Expertise</div>
        <h2 className="section-title">The Architect's <em>toolkit</em></h2>
        <div className="skills-grid">
          <div className="skill-cat fade-up">
            <div className="skill-icon">☁️</div>
            <h4>Cloud &amp; Infrastructure</h4>
            <ul>
              <li>
                <div className="skill-item-header">
                  <span>AWS, GCP &amp; Azure Multi-Cloud</span>
                  <span className="skill-level">Master</span>
                </div>
                <div className="skill-bar"><div className="skill-progress" style={{ width: '95%' }}></div></div>
              </li>
              <li>
                <div className="skill-item-header">
                  <span>Kubernetes &amp; Serverless</span>
                  <span className="skill-level">Expert</span>
                </div>
                <div className="skill-bar"><div className="skill-progress" style={{ width: '90%' }}></div></div>
              </li>
              <li>
                <div className="skill-item-header">
                  <span>FinOps &amp; Cloud Governance</span>
                  <span className="skill-level">Expert</span>
                </div>
                <div className="skill-bar"><div className="skill-progress" style={{ width: '92%' }}></div></div>
              </li>
            </ul>
          </div>
          <div className="skill-cat fade-up">
            <div className="skill-icon">🤖</div>
            <h4>AI &amp; Innovation</h4>
            <ul>
              <li>
                <div className="skill-item-header">
                  <span>Claude &amp; Anthropic AI (CCA-F)</span>
                  <span className="skill-level">Certified</span>
                </div>
                <div className="skill-bar"><div className="skill-progress" style={{ width: '96%' }}></div></div>
              </li>
              <li>
                <div className="skill-item-header">
                  <span>Generative AI &amp; Enterprise LLMs</span>
                  <span className="skill-level">Master</span>
                </div>
                <div className="skill-bar"><div className="skill-progress" style={{ width: '94%' }}></div></div>
              </li>
              <li>
                <div className="skill-item-header">
                  <span>RAG &amp; Vector Databases</span>
                  <span className="skill-level">Expert</span>
                </div>
                <div className="skill-bar"><div className="skill-progress" style={{ width: '88%' }}></div></div>
              </li>
              <li>
                <div className="skill-item-header">
                  <span>MLOps &amp; Model Governance</span>
                  <span className="skill-level">Expert</span>
                </div>
                <div className="skill-bar"><div className="skill-progress" style={{ width: '85%' }}></div></div>
              </li>
            </ul>
          </div>
          <div className="skill-cat fade-up">
            <div className="skill-icon">🏗️</div>
            <h4>Leadership &amp; Strategy</h4>
            <ul>
              <li>
                <div className="skill-item-header">
                  <span>TOGAF Enterprise Architecture</span>
                  <span className="skill-level">Certified</span>
                </div>
                <div className="skill-bar"><div className="skill-progress" style={{ width: '95%' }}></div></div>
              </li>
              <li>
                <div className="skill-item-header">
                  <span>Digital Transformation Strategy</span>
                  <span className="skill-level">Master</span>
                </div>
                <div className="skill-bar"><div className="skill-progress" style={{ width: '92%' }}></div></div>
              </li>
              <li>
                <div className="skill-item-header">
                  <span>Executive Stakeholder Management</span>
                  <span className="skill-level">Expert</span>
                </div>
                <div className="skill-bar"><div className="skill-progress" style={{ width: '90%' }}></div></div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* INSIGHTS */}
      <section className="blog-section" id="insights">
        <div className="section-label">Thinking</div>
        <h2 className="section-title">Architect's <em>insights</em></h2>
        <div className="blog-grid">
          <div className="blog-card fade-up">
            <div className="blog-cat">AI · Business Value</div>
            <h3>The Real ROI of Enterprise Generative AI</h3>
            <p>Moving past the hype cycle: How leading cloud architects integrate LLM microservices with strict governance to create actual bottom-line value.</p>
            <button className="blog-link-btn" onClick={() => {
              setSelectedCaseStudy({
                id: 'insight1',
                cat: 'Thought Leadership · Enterprise AI',
                title: 'The Real ROI of Enterprise Generative AI',
                metric: 'Executive Whitepaper',
                icon: '💡',
                badgeClass: 'thumb-ai',
                summary: 'An executive synthesis on evaluating, governing, and operationalizing Generative AI within large-scale enterprise environments.',
                challenge: 'Many enterprises struggle to move GenAI pilots into production due to governance concerns, unpredictable API costs, and security risks.',
                solution: 'Implement an architectural framework centered on RAG pipelines, internal model routing, strict token budgeting, and zero-data-retention agreements.',
                impacts: [
                  'Reduces LLM inference cost overhead by up to 45% through intelligent query caching',
                  'Ensures enterprise compliance and data privacy with self-hosted embeddings',
                  'Provides measurable KPIs for productivity improvements across business domains'
                ],
                tech: ['Generative AI', 'Enterprise Architecture', 'FinOps', 'Data Governance']
              });
            }}>Read Summary →</button>
          </div>
          <div className="blog-card fade-up">
            <div className="blog-cat">Architecture · Governance</div>
            <h3>Resilient Governance in Multi-Cloud Environments</h3>
            <p>Building scalable enterprise architecture standards that survive rapid technology churn without creating organizational bottlenecks.</p>
            <button className="blog-link-btn" onClick={() => {
              setSelectedCaseStudy({
                id: 'insight2',
                cat: 'Thought Leadership · Cloud Governance',
                title: 'Resilient Governance in Multi-Cloud Environments',
                metric: 'Architectural Guide',
                icon: '🏛️',
                badgeClass: 'thumb-retail',
                summary: 'Principles for establishing robust architectural guardrails without slowing down agile engineering teams.',
                challenge: 'Rigid architecture review boards create delivery bottlenecks, while zero governance results in technical debt and security vulnerabilities.',
                solution: 'Shift governance left by codifying architecture decision records (ADRs), automating policy enforcement via IaC linting, and establishing FinOps budgets.',
                impacts: [
                  'Cuts architectural approval lead times from weeks to hours',
                  'Ensures 100% compliance across AWS, GCP, and Azure landing zones',
                  'Fosters engineering autonomy backed by automated policy guardrails'
                ],
                tech: ['TOGAF', 'Multi-Cloud', 'Infrastructure as Code', 'Policy as Code']
              });
            }}>Read Summary →</button>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="contact-section" id="contact">
        <div className="contact-card fade-up">
          <div className="section-label" style={{ justifyContent: 'center' }}>Get in touch</div>
          <h2>Ready to discuss your<br /><em>next transformation?</em></h2>
          <p>Let's talk strategy. Whether it's multi-cloud architecture, AI enablement, or enterprise modernization — I am always open to high-impact opportunities.</p>
          <div className="contact-links print-hide">
            <a href="mailto:tirtha2u@gmail.com" className="contact-link">
              <span>✉️</span> tirtha2u@gmail.com
            </a>
            <a href="https://www.linkedin.com/in/tirthankar-bhadra-18422447" target="_blank" rel="noopener noreferrer" className="contact-link">
              <span>🔗</span> LinkedIn Profile
            </a>
            <a href="tel:+919831950061" className="contact-link">
              <span>📱</span> +91 9831950061
            </a>
            <button onClick={() => setContactModalOpen(true)} className="contact-link primary-contact-btn">
              <span>💬</span> Send Quick Message
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <span className="footer-logo">TB<span>.</span></span>
        <p>© {new Date().getFullYear()} Tirthankar Bhadra. Enterprise Architect &amp; AI Leader.</p>
        <p>Kolkata, West Bengal, India</p>
      </footer>

      {/* BACK TO TOP BUTTON */}
      {showScrollTop && (
        <button
          className="scroll-top-btn print-hide"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          title="Back to Top"
        >
          ↑
        </button>
      )}

      {/* CASE STUDY MODAL */}
      {selectedCaseStudy && (
        <div className="modal-backdrop" onClick={() => setSelectedCaseStudy(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedCaseStudy(null)}>×</button>
            <div className="modal-header">
              <span className="modal-icon">{selectedCaseStudy.icon}</span>
              <div>
                <div className="modal-cat">{selectedCaseStudy.cat}</div>
                <h2>{selectedCaseStudy.title}</h2>
              </div>
            </div>
            <div className="modal-body">
              <div className="modal-highlight-box">
                <strong>Key Result:</strong> {selectedCaseStudy.metric}
              </div>

              <h3>Overview</h3>
              <p>{selectedCaseStudy.summary}</p>

              <h3>The Business Challenge</h3>
              <p>{selectedCaseStudy.challenge}</p>

              <h3>Architectural Solution</h3>
              <p>{selectedCaseStudy.solution}</p>

              <h3>Key Outcomes &amp; Impact</h3>
              <ul className="modal-impact-list">
                {selectedCaseStudy.impacts.map((item, idx) => (
                  <li key={idx}><strong>Outcome {idx + 1}:</strong> {item}</li>
                ))}
              </ul>

              <h3>Technology &amp; Domain Stack</h3>
              <div className="tags">
                {selectedCaseStudy.tech.map((t, idx) => (
                  <span key={idx} className="tag">{t}</span>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setSelectedCaseStudy(null)}>Close Case Study</button>
              <button className="btn btn-primary" onClick={() => {
                setSelectedCaseStudy(null);
                setContactModalOpen(true);
              }}>Discuss Similar Engagement →</button>
            </div>
          </div>
        </div>
      )}

      {/* CONTACT MODAL */}
      {contactModalOpen && (
        <div className="modal-backdrop" onClick={() => setContactModalOpen(false)}>
          <div className="modal-content contact-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setContactModalOpen(false)}>×</button>
            <div className="modal-header">
              <span className="modal-icon">💬</span>
              <div>
                <div className="modal-cat">Direct Contact</div>
                <h2>Let's Connect</h2>
              </div>
            </div>
            <div className="modal-body">
              {formSubmitted ? (
                <div className="form-success-state">
                  <div className="success-icon">✓</div>
                  <h3>Message Sent Successfully!</h3>
                  <p>Thank you for reaching out, Tirthankar will respond promptly.</p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="contact-form">
                  <div className="form-group">
                    <label>Your Name</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Sarah Jenkins"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Your Email Address</label>
                    <input
                      type="email"
                      required
                      placeholder="name@company.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Subject</label>
                    <select
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    >
                      <option value="Enterprise Architecture Inquiry">Enterprise Architecture Inquiry</option>
                      <option value="AI & Cloud Advisory">AI &amp; Cloud Advisory</option>
                      <option value="Executive Leadership / Recruitment">Executive Leadership / Opportunity</option>
                      <option value="Speaking / Technical Advisory">Speaking / Technical Advisory</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Message</label>
                    <textarea
                      rows={4}
                      required
                      placeholder="Briefly describe your project or inquiry..."
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    ></textarea>
                  </div>
                  <div className="modal-footer" style={{ padding: 0, marginTop: '1.5rem', background: 'none' }}>
                    <button type="button" className="btn btn-outline" onClick={() => setContactModalOpen(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Send Message →</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;

